import React from 'react';
import NamedBox from '../atoms/NamedBox';
import CircleBadge from '../atoms/CircleBadge';
import { COLOR_BLACK, COLOR_PURPLE } from '../styles/colors';
import Accordion from '../atoms/Accordion';
import ListItem from '../atoms/ListItem';
import SpeedRecruitIcon from '../icons/SpeedRecruitIcon';
import { PlayerCollegeMPH } from '../types/PlayerCollegeMPH';
import { Grid, makeStyles, Theme, Tooltip } from '@material-ui/core';
import ProgressLine from '../atoms/ProgressLine';
import EllipsisIcon from '../icons/EllipsisIcon';
import { Link } from 'react-router-dom';
import PFFIcon from '../icons/PFFIcon'
const BETTER_THAN_TOOLTIP_TEXT = 'Better than D-I position';

interface PFFPlayerInGameRatingProps {
  collegeInGameStats: PlayerCollegeMPH[];
  linkPffUrl?: string;
  className?: string;
}

const useStyles = makeStyles((theme: Theme) => ({
  pffIcon: {
    width: '142px',
    height: '30px',
    display: 'block',
    flexShrink: 0,
    flexGrow: 0,     
    marginBottom: '10px',
  },
  header: {
    alignItems: 'center',
    color: COLOR_BLACK,
    display: 'flex',
    fontWeight: 600,
    gap: '12px',
    width: '100%'
  },
  progressLineWrapper: {
    width: '100%',
  },
  progressLine: {
    maxWidth: 350,
    paddingRight: 0,
    marginLeft: 'auto',
  },
  badgeWrap: {
    marginRight: 10,
    width: '45px',
  },
  badgeVal: {
    color: COLOR_BLACK,
  },
  linkUrl: {
    display: 'inline-block',
    fontWeight: 'bold',
    maxWidth: 200,
    minWidth: 200,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  headeName: {
    flexGrow: 1,
  },
}), {
  name: 'PFFPlayerInGameRating',
});

export default function PFFPlayerInGameRating(props: PFFPlayerInGameRatingProps) {
  const {
    collegeInGameStats,
    linkPffUrl,
    className,
  } = props;

  const classes = useStyles();

  const sortedCollegeStats = [...collegeInGameStats].sort(
    (a, b) => (b.year ?? 0) - (a.year ?? 0)
  );

  const careerBest = sortedCollegeStats.reduce<PlayerCollegeMPH | null>(
    (best, season) =>
      (season.mph ?? 0) > (best?.mph ?? 0) ? season : best,
    null
  );

  return (
    <NamedBox
      className={className}
      headerNameClassName={classes.headeName}
      name={
        <div className={classes.header}>
          <CircleBadge
            className={classes}
            value="MPH"
            height={45}
            width={45}
            color={COLOR_PURPLE}
          />
          <span>College In-Game Rating</span>
        </div>
      }
      headerContent={<PFFIcon className={classes.pffIcon} viewBox="0 0 340 25"/>}
    >
      {sortedCollegeStats.length > 0 &&
        sortedCollegeStats.map((season: PlayerCollegeMPH, index) => {
          const {
            year = 0,
            mph = 0,
            mphPercentile = 0,
            distanceTraveled = 0,
            mphPositionRank = 0,
            mphPosition = '',
            timesExceeded20 = 0,
            averageTop5MPH = 0,
            ncaaMPHRank = 0,
            ncaaMPHPercentile = 0,
          } = season;

          return (
            <Accordion
              key={year || index}
              summary={`Film Year: ${year} (${mphPosition})`}
              expanded={index === 0}
            >
              {/* Season Max MPH */}
              <ListItem title="Season Max MPH:">
                <Grid container>
                  <Grid item lg={1} xs={2} />
                  <Grid item lg={1} xs={2} />
                  <Grid item lg={3} xs={5}>
                    <b>{mph}</b>
                  </Grid>
                </Grid>
              </ListItem>

              {/* Play Distance */}
              <ListItem title="Play Distance:">
                <Grid container>
                  <Grid item lg={1} xs={2} />
                  <Grid item lg={1} xs={2} />
                  <Grid item lg={3} xs={5}>
                    <b>{distanceTraveled} yds.</b>
                  </Grid>
                </Grid>
              </ListItem>

              {/* Position MPH % */}
              <ListItem title="Position MPH %:">
                <Grid container>
                  <Grid item lg={1} xs={2}>
                    {mphPercentile >= 75 && (
                      <Tooltip title={BETTER_THAN_TOOLTIP_TEXT}>
                        <SpeedRecruitIcon className="purple" />
                      </Tooltip>
                    )}
                  </Grid>
                  <Grid item lg={1} xs={2} />
                  <Grid lg={3} xs={5}>
                    <b>{mphPercentile}%</b>
                  </Grid>
                  <Grid item lg xs>
                    <div className={classes.progressLineWrapper}>
                      <ProgressLine
                        className={classes.progressLine}
                        value={mphPercentile}
                      />
                    </div>
                  </Grid>
                </Grid>
              </ListItem>

              {/* Position Rank */}
              <ListItem title="Position Rank:">
                <Grid container>
                  <Grid item lg={1} xs={2} />
                  <Grid item lg={1} xs={2} />
                  <Grid item lg={3} xs={5}>
                    <b>{mphPositionRank}</b>
                  </Grid>
                </Grid>
              </ListItem>

              {/* NCAA MPH % */}
              <ListItem title="NCAA MPH %:">
                <Grid container>
                  <Grid item lg={1} xs={2} />
                  <Grid item lg={1} xs={2} />
                  <Grid lg={3} xs={5}>
                    <b>{ncaaMPHPercentile}%</b>
                  </Grid>
                  <Grid item lg xs>
                    <div className={classes.progressLineWrapper}>
                      <ProgressLine
                        className={classes.progressLine}
                        value={ncaaMPHPercentile}
                      />
                    </div>
                  </Grid>
                </Grid>
              </ListItem>

              {/* NCAA Rank */}
              <ListItem title="NCAA Rank:">
                <Grid container>
                  <Grid item lg={1} xs={2} />
                  <Grid item lg={1} xs={2} />
                  <Grid item lg={3} xs={5}>
                    <b>{ncaaMPHRank}</b>
                  </Grid>
                </Grid>
              </ListItem>

              {/* Top 5 MPH Avg */}
              <ListItem icon={EllipsisIcon} title="Top 5 MPH Avg:">
                <Grid container>
                  <Grid item lg={1} xs={2} />
                  <Grid item lg={3} xs={5}>
                    <b>{averageTop5MPH || 'N/A'}</b>
                  </Grid>
                </Grid>
              </ListItem>

              {/* Times 20+ MPH */}
              <ListItem title="Times 20+ MPH:">
                <Grid container>
                  <Grid item lg={1} xs={2} />
                  <Grid item lg={1} xs={2} />
                  <Grid item lg={3} xs={5}>
                    <b>{timesExceeded20 || 'N/A'}</b>
                  </Grid>
                </Grid>
              </ListItem>

              {/* Career Best */}
              <ListItem title="Career Best MPH:">
                <Grid container>
                  <Grid item lg={1} xs={2} />
                  <Grid item lg={1} xs={2} />
                  <Grid item lg={3} xs={5}>
                    <b>
                      {careerBest?.mph ?? 0}
                      {careerBest?.year ? ` (${careerBest.year})` : ''}
                    </b>
                  </Grid>
                </Grid>
              </ListItem>

              {/* Player URL */}
              {linkPffUrl && (
                <ListItem title="Player PFF URL:">
                  <Grid container>
                    <Grid item lg={1} xs={2} />
                    <Grid item lg={1} xs={2} />
                    <Grid item lg={3} xs={5}>
                      <Link
                        className={classes.linkUrl}
                        to={linkPffUrl}
                      >
                        {linkPffUrl}
                      </Link>
                      {/* <b>
                        <a
                          href={linkPffUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {linkPffUrl}
                        </a>
                      </b> */}
                    </Grid>
                  </Grid>
                </ListItem>
              )}
            </Accordion>
          );
        })}
    </NamedBox>
  );
}
